import * as React from "react";

import {
  GetCountriesReferentialQuery,
  PersonDto,
  ReferentialApi,
} from "../../../api";
import { useGlobalData } from "../../../contexts";
import { ETLCodes } from "../../../locales";
import { FusionCheckbox } from "../components/FusionCheckbox";
import { FusionCombo } from "../components/FusionCombo";
import { FusionRadio } from "../components/FusionRadio";

export interface IPersonFusionProps {
  person1: PersonDto;
  person2: PersonDto;
  selectLeft?: boolean;
}

export const PersonFusion: React.FunctionComponent<IPersonFusionProps> = ({
  person1,
  person2,
  selectLeft = null,
}) => {
  const { currentLanguage } = useGlobalData();

  return (
    <>
      {/*BcIdentifier*/}
      <FusionRadio
        elementName="fusingPerson1.bcIdentifier"
        elementNameBis="fusingPerson2.bcIdentifier"
        fusedElementName="fusion.fusion.bcIdentifier"
        label={ETLCodes.BCIdentifier}
        elementValue={person1.bcIdentifier}
        elementValueBis={person2.bcIdentifier}
        selectLeft={selectLeft}
      />
      {/*Honorific*/}
      <FusionCombo
        elementName="fusingPerson1.personHonorificId"
        elementNameBis="fusingPerson2.personHonorificId"
        fusedElementName="fusion.fusion.personHonorificId"
        label={ETLCodes.Salutation}
        elementValue={person1.personHonorificId}
        elementValueBis={person2.personHonorificId}
        referentialFunc={(refApi: ReferentialApi) =>
          refApi.getPersonSalutations(currentLanguage.languageId)
        }
        selectLeft={selectLeft}
      />
      {/*LastName*/}
      <FusionRadio
        elementName="fusingPerson1.lastName"
        elementNameBis="fusingPerson2.lastName"
        fusedElementName="fusion.fusion.lastName"
        label={ETLCodes.LastName}
        elementValue={person1.lastName}
        elementValueBis={person2.lastName}
        selectLeft={selectLeft}
      />
      {/*FirstName*/}
      <FusionRadio
        elementName="fusingPerson1.firstName"
        elementNameBis="fusingPerson2.firstName"
        fusedElementName="fusion.fusion.firstName"
        label={ETLCodes.FirstName}
        elementValue={person1.firstName}
        elementValueBis={person2.firstName}
        selectLeft={selectLeft}
      />
      {/*Email*/}
      <FusionRadio
        elementName="fusingPerson1.email"
        elementNameBis="fusingPerson2.email"
        fusedElementName="fusion.fusion.email"
        label={ETLCodes.Email}
        elementValue={person1.email}
        elementValueBis={person2.email}
        selectLeft={selectLeft}
      />
      {/*PhoneNumber*/}
      <FusionRadio
        elementName="fusingPerson1.phoneNumber"
        elementNameBis="fusingPerson2.phoneNumber"
        fusedElementName="fusion.fusion.phoneNumber"
        label={ETLCodes.PhoneNumber}
        elementValue={person1.phoneNumber}
        elementValueBis={person2.phoneNumber}
        selectLeft={selectLeft}
      />
      {/*MobilePhoneNumber*/}
      <FusionRadio
        elementName="fusingPerson1.mobilePhoneNumber"
        elementNameBis="fusingPerson2.mobilePhoneNumber"
        fusedElementName="fusion.fusion.mobilePhoneNumber"
        label={ETLCodes.MobilePhoneNumber}
        elementValue={person1.mobilePhoneNumber}
        elementValueBis={person2.mobilePhoneNumber}
        selectLeft={selectLeft}
      />
      {/*Language*/}
      <FusionCombo
        elementName="fusingPerson1.languageId"
        elementNameBis="fusingPerson2.languageId"
        fusedElementName="fusion.fusion.languageId"
        label={ETLCodes.Language}
        elementValue={person1.languageId}
        elementValueBis={person2.languageId}
        referentialFunc={(refApi: ReferentialApi) => refApi.getLanguages()}
        selectLeft={selectLeft}
      />
      {/*DeathDate*/}
      <FusionRadio
        elementName="fusingPerson1.deathDate"
        elementNameBis="fusingPerson2.deathDate"
        fusedElementName="fusion.fusion.deathDate"
        label={ETLCodes.DeathDate}
        elementValue={person1.deathDate}
        elementValueBis={person2.deathDate}
        isDate
        selectLeft={selectLeft}
      />
      {/*IsBadPayer*/}
      <FusionCheckbox
        elementName="fusingPerson1.isBadPayer"
        elementNameBis="fusingPerson2.isBadPayer"
        fusedElementName="fusion.fusion.isBadPayer"
        label={ETLCodes.IsBadPayer}
        elementValue={person1.isBadPayer}
        elementValueBis={person2.isBadPayer}
        selectLeft={selectLeft}
      />
      {/*BannedFrom*/}
      <FusionRadio
        elementName="fusingPerson1.bannedFrom"
        elementNameBis="fusingPerson2.bannedFrom"
        fusedElementName="fusion.fusion.bannedFrom"
        label={ETLCodes.BannedFrom}
        elementValue={person1.bannedFrom}
        elementValueBis={person2.bannedFrom}
        isDate
        selectLeft={selectLeft}
      />
      {/*BannedTo*/}
      <FusionRadio
        elementName="fusingPerson1.bannedTo"
        elementNameBis="fusingPerson2.bannedTo"
        fusedElementName="fusion.fusion.bannedTo"
        label={ETLCodes.BannedTo}
        elementValue={person1.bannedTo}
        elementValueBis={person2.bannedTo}
        isDate
        selectLeft={selectLeft}
      />
      {/*Number*/}
      <FusionRadio
        elementName="fusingPerson1.number"
        elementNameBis="fusingPerson2.number"
        fusedElementName="fusion.fusion.number"
        label={ETLCodes.Number}
        elementValue={person1.number}
        elementValueBis={person2.number}
        selectLeft={selectLeft}
      />
      {/*Street*/}
      <FusionRadio
        elementName="fusingPerson1.street"
        elementNameBis="fusingPerson2.street"
        fusedElementName="fusion.fusion.street"
        label={ETLCodes.Street}
        elementValue={person1.street}
        elementValueBis={person2.street}
        selectLeft={selectLeft}
      />
      {/*PostalCode*/}
      <FusionRadio
        elementName="fusingPerson1.postalCode"
        elementNameBis="fusingPerson2.postalCode"
        fusedElementName="fusion.fusion.postalCode"
        label={ETLCodes.PostalCode}
        elementValue={person1.postalCode}
        elementValueBis={person2.postalCode}
        selectLeft={selectLeft}
      />
      {/*City*/}
      <FusionRadio
        elementName="fusingPerson1.city"
        elementNameBis="fusingPerson2.city"
        fusedElementName="fusion.fusion.city"
        label={ETLCodes.City}
        elementValue={person1.city}
        elementValueBis={person2.city}
        selectLeft={selectLeft}
      />
      {/*Country*/}
      <FusionCombo
        elementName="fusingPerson1.countryId"
        elementNameBis="fusingPerson2.countryId"
        fusedElementName="fusion.fusion.countryId"
        label={ETLCodes.Country}
        elementValue={person1.countryId}
        elementValueBis={person2.countryId}
        referentialFunc={(refApi: ReferentialApi) =>
          refApi.getCountries(new GetCountriesReferentialQuery())
        }
        selectLeft={selectLeft}
      />
      {/*Remark*/}
      <FusionRadio
        elementName="fusingPerson1.remark"
        elementNameBis="fusingPerson2.remark"
        fusedElementName="fusion.fusion.remark"
        label={ETLCodes.Remark}
        elementValue={person1.remark}
        elementValueBis={person2.remark}
        selectLeft={selectLeft}
      />
      {/*VATNumber*/}
      <FusionRadio
        elementName="fusingPerson1.vatNumber"
        elementNameBis="fusingPerson2.vatNumber"
        fusedElementName="fusion.fusion.vatNumber"
        label={ETLCodes.VATNumber}
        elementValue={person1.vatNumber}
        elementValueBis={person2.vatNumber}
        selectLeft={selectLeft}
      />
      {/*SocietyName*/}
      <FusionRadio
        elementName="fusingPerson1.societyName"
        elementNameBis="fusingPerson2.societyName"
        fusedElementName="fusion.fusion.societyName"
        label={ETLCodes.SocietyName}
        elementValue={person1.societyName}
        elementValueBis={person2.societyName}
        selectLeft={selectLeft}
      />
    </>
  );
};
